import React, { useEffect, useState, useCallback } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import api from '../api/axios';
import type { EntityType } from '../types/prompts';
import EntityTypeSidebar from './EntityTypeSidebar';
import EntityTypeDetail from './EntityTypeDetail';
import CreateEntityTypeDialog from './CreateEntityTypeDialog';

export default function EntityTypeManager() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [entityTypes, setEntityTypes] = useState<EntityType[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const selectedSlug = searchParams.get('entity_type') ||
    localStorage.getItem('entityTypeDetail_entityType') || '';

  const loadEntityTypes = useCallback(async () => {
    try {
      const res = await api.get('/schemes/prompts/entity-types/');
      const types = res.data.results || res.data;
      setEntityTypes(Array.isArray(types) ? types : []);
    } catch (err) {
      console.error('Failed to load entity types:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEntityTypes();
  }, [loadEntityTypes]);

  // Auto-select first type if none selected
  useEffect(() => {
    if (!loading && entityTypes.length > 0 && !selectedSlug) {
      handleSelect(entityTypes[0].slug);
    }
  }, [loading, entityTypes, selectedSlug]);

  const handleSelect = (slug: string) => {
    const tab = searchParams.get('tab');
    const params: Record<string, string> = { entity_type: slug };
    if (tab) params.tab = tab;
    setSearchParams(params);
    localStorage.setItem('entityTypeDetail_entityType', slug);
  };

  const handleCreated = async (slug: string) => {
    await loadEntityTypes();
    handleSelect(slug);
  };

  const handleEntityTypeUpdated = () => {
    loadEntityTypes();
  };

  const handleEntityTypeDeleted = async () => {
    await loadEntityTypes();
    // Select first remaining type, or clear selection
    const res = await api.get('/schemes/prompts/entity-types/');
    const types = res.data.results || res.data;
    const remaining = Array.isArray(types) ? types : [];
    if (remaining.length > 0) {
      handleSelect(remaining[0].slug);
    } else {
      setSearchParams({ tab: searchParams.get('tab') || '' });
    }
  };

  const handleReorder = async (slugs: string[]) => {
    // Optimistic update
    const reordered = slugs.map(slug => entityTypes.find(et => et.slug === slug)!).filter(Boolean);
    setEntityTypes(reordered);

    try {
      await api.post('/schemes/prompts/entity-types/reorder/', { slugs });
    } catch (err) {
      console.error('Failed to save order:', err);
      loadEntityTypes(); // Revert on failure
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const selectedType = entityTypes.find(et => et.slug === selectedSlug) || null;

  return (
    <>
      <Box sx={{ display: 'flex', height: 'calc(100vh - 160px)', border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
        <EntityTypeSidebar
          entityTypes={entityTypes}
          selectedSlug={selectedSlug}
          onSelect={handleSelect}
          onCreateNew={() => setCreateDialogOpen(true)}
          onReorder={handleReorder}
        />
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {selectedType ? (
            <EntityTypeDetail
              entityType={selectedType}
              allEntityTypes={entityTypes}
              onEntityTypeUpdated={handleEntityTypeUpdated}
              onEntityTypeDeleted={handleEntityTypeDeleted}
            />
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: 'text.secondary' }}>
              Select an entity type from the sidebar
            </Box>
          )}
        </Box>
      </Box>

      <CreateEntityTypeDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreated={handleCreated}
      />
    </>
  );
}
